import { DependencyList, ReactNode, useCallback, useEffect, useState } from 'react';
import { Credentials, errorMessage } from '../api';

export function Alert({ type, children }: { type: 'success' | 'error' | 'info'; children: ReactNode }) {
  return <div className={`form-alert ${type}`}>{children}</div>;
}

const STATUS_CLASS: Record<string, string> = {
  active: 'tag-completed', paid: 'tag-completed', completed: 'tag-completed',
  provisioning: 'tag-progress', pending: 'tag-progress', created: 'tag-progress', running: 'tag-progress',
  failed: 'tag-failed', declined: 'tag-failed', error: 'tag-failed',
};

export function StatusTag({ status }: { status: string }) {
  return <span className={`tag ${STATUS_CLASS[status] ?? 'tag-muted'}`}>{status}</span>;
}

export function Spinner({ small }: { small?: boolean }) {
  return <div className={`spinner${small ? ' small' : ''}`} role="status" aria-label="Loading" />;
}

export function Loading({ text = 'Loading…' }: { text?: string }) {
  return <div className="empty-state"><Spinner />{text}</div>;
}

/** Shows freshly generated credentials. They are not stored anywhere, so this is the only chance to copy them. */
export function CredentialsBox({ credentials, onClose }: { credentials: Credentials; onClose: () => void }) {
  return (
    <div className="credentials-box">
      <p>Copy these now — the password will not be shown again.</p>
      <div className="cred-row"><span>Username</span><span className="mono">{credentials.username}</span></div>
      <div className="cred-row">
        <span>Password</span><span className="mono">{credentials.password}</span>
        <button className="link-button" onClick={() => navigator.clipboard?.writeText(credentials.password)}>Copy</button>
      </div>
      <div className="btn-row" style={{ marginTop: '0.75rem' }}>
        <button className="btn-outline small" onClick={onClose}>Done</button>
      </div>
    </div>
  );
}

/** Loads data on mount / when deps change, with a reload function for after mutations. */
export function useLoad<T>(load: () => Promise<T>, deps: DependencyList) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(load, deps);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      setData(await run());
      setError(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [run]);

  useEffect(() => { void reload(); }, [reload]);

  return { data, setData, error, loading, reload };
}

/** Wraps an async action with busy and error state. */
export function useAction() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const run = useCallback(async <R,>(fn: () => Promise<R>): Promise<R | undefined> => {
    setBusy(true);
    setError(null);
    try {
      return await fn();
    } catch (err) {
      setError(errorMessage(err));
      return undefined;
    } finally {
      setBusy(false);
    }
  }, []);

  return { busy, error, setError, run };
}
